import { useState, useEffect, useMemo } from 'react';
import { ArrowRight, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { FormField, Input, Label, FormError } from '../ui/input';
import { DateInput } from '../ui/date-input';
import { Select } from '../ui/select-custom';
import { toISODate, fromISODate } from '../../lib/date';
import { formatCurrency, formatQuantity } from '../../lib/format';
import { computePositions } from '../../lib/investments';
import type { PortfolioInput } from '../../lib/investments';
import type { InvestmentAsset } from '../../types';
import type { ComponentProps } from 'react';

export interface ConversionFormData {
  fromAssetId: string;
  toAssetId: string;
  date: Date;
  fromQuantity: number;
  toQuantity: number;
  notes: string;
}

export interface ConversionFormProps extends Omit<ComponentProps<'form'>, 'onSubmit'> {
  onSubmit: (data: ConversionFormData) => void;
  onClose?: () => void;
  assets: InvestmentAsset[];
  portfolio: PortfolioInput;
}

export function ConversionForm({
  onSubmit,
  onClose,
  assets,
  portfolio,
  ...props
}: ConversionFormProps) {
  const [fromAssetId, setFromAssetId] = useState('');
  const [toAssetId, setToAssetId] = useState('');
  const [dateISO, setDateISO] = useState(toISODate(new Date()));
  const [fromQuantity, setFromQuantity] = useState('');
  const [toQuantity, setToQuantity] = useState('');
  const [notes, setNotes] = useState('');

  const date = useMemo(() => (dateISO ? fromISODate(dateISO) : null), [dateISO]);

  // Posição do papel antigo na data, para sugerir a quantidade e mostrar o custo.
  const fromPosition = useMemo(() => {
    if (!fromAssetId || !date) return null;
    return (
      computePositions(portfolio, date).find((p) => p.asset.id === fromAssetId) ?? null
    );
  }, [portfolio, fromAssetId, date]);

  useEffect(() => {
    if (fromPosition && fromQuantity === '') {
      setFromQuantity(String(fromPosition.quantity));
    }
  }, [fromPosition, fromQuantity]);

  const fromNumber = Number(fromQuantity.replace(',', '.'));
  const toNumber = Number(toQuantity.replace(',', '.'));
  const hasFrom = fromQuantity.trim() !== '' && Number.isFinite(fromNumber) && fromNumber > 0;
  const hasTo = toQuantity.trim() !== '' && Number.isFinite(toNumber) && toNumber > 0;

  const available = fromPosition?.quantity ?? 0;
  const exceeds = hasFrom && fromNumber > available + 1e-8;
  const sameAsset = fromAssetId !== '' && fromAssetId === toAssetId;

  // O custo que viaja é proporcional às cotas que saem.
  const movingCost =
    fromPosition && hasFrom ? fromPosition.averageCost * fromNumber : 0;
  const newAverage = hasTo && movingCost > 0 ? movingCost / toNumber : 0;

  const isFormValid =
    fromAssetId !== '' && toAssetId !== '' && !sameAsset && hasFrom && hasTo && !!date && !exceeds;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || !date) return;
    onSubmit({
      fromAssetId,
      toAssetId,
      date,
      fromQuantity: fromNumber,
      toQuantity: toNumber,
      notes: notes.trim(),
    });
  };

  const options = assets.map((a) => ({ value: a.id, label: a.ticker }));

  return (
    <form
      data-slot="conversion-form"
      onSubmit={handleSubmit}
      className="flex flex-col gap-4"
      {...props}
    >
      <div className="flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
        <Info className="mt-0.5 size-3.5 shrink-0" />
        <span>
          Para incorporação de fundo, troca de ticker ou cisão. O custo de aquisição
          viaja junto com as cotas e <strong>nenhum ganho é realizado</strong> — por isso
          não se lança como venda seguida de compra, que inflaria as vendas do mês.
        </span>
      </div>

      <div className="grid grid-cols-[1fr_auto_1fr] items-end gap-2">
        <FormField>
          <Label>
            Papel que sai <span className="text-destructive">*</span>
          </Label>
          <Select
            value={fromAssetId}
            onChange={setFromAssetId}
            options={options}
            placeholder="Selecione"
            className="w-full justify-between"
          />
        </FormField>
        <ArrowRight className="mb-2.5 size-4 shrink-0 text-muted-foreground" />
        <FormField>
          <Label>
            Papel que entra <span className="text-destructive">*</span>
          </Label>
          <Select
            value={toAssetId}
            onChange={setToAssetId}
            options={options}
            placeholder="Selecione"
            className="w-full justify-between"
          />
        </FormField>
      </div>

      {sameAsset && <FormError>Escolha dois papéis diferentes.</FormError>}

      <FormField>
        <Label htmlFor="conversion-date">
          Data <span className="text-destructive">*</span>
        </Label>
        <DateInput id="conversion-date" value={dateISO} onChange={setDateISO} />
      </FormField>

      <div className="grid grid-cols-2 gap-4">
        <FormField>
          <Label htmlFor="fromQuantity">
            Cotas que saem <span className="text-destructive">*</span>
          </Label>
          <Input
            id="fromQuantity"
            value={fromQuantity}
            inputMode="decimal"
            placeholder="Ex: 50"
            onChange={(e) => setFromQuantity(e.target.value.replace(/[^0-9.,]/g, ''))}
            error={exceeds}
          />
          {fromPosition && (
            <span className="text-xs text-muted-foreground">
              Posição na data: {formatQuantity(fromPosition.quantity)} cotas, custo R${' '}
              {formatCurrency(fromPosition.totalCost)}
            </span>
          )}
          {exceeds && <FormError>Mais cotas do que a posição na data ({formatQuantity(available)}).</FormError>}
        </FormField>

        <FormField>
          <Label htmlFor="toQuantity">
            Cotas que entram <span className="text-destructive">*</span>
          </Label>
          <Input
            id="toQuantity"
            value={toQuantity}
            inputMode="decimal"
            placeholder="Ex: 43"
            onChange={(e) => setToQuantity(e.target.value.replace(/[^0-9.,]/g, ''))}
          />
          <span className="text-xs text-muted-foreground">
            Inclua a fração, se houver (ex: 43,71). Depois lance o leilão dela como
            venda — assim o custo da fração sai junto.
          </span>
        </FormField>
      </div>

      <FormField>
        <Label htmlFor="conversion-notes">Observação</Label>
        <Input
          id="conversion-notes"
          value={notes}
          placeholder="Ex: incorporação do fundo"
          onChange={(e) => setNotes(e.target.value)}
        />
      </FormField>

      {movingCost > 0 && hasTo && (
        <div className="flex flex-col gap-1 rounded-lg bg-muted px-3 py-2 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Custo que migra</span>
            <span className="font-semibold text-foreground">
              R$ {formatCurrency(movingCost)}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Novo custo médio</span>
            <span>R$ {formatCurrency(newAverage)} por cota</span>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        {onClose && (
          <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
            Cancelar
          </Button>
        )}
        <Button type="submit" className="flex-1" disabled={!isFormValid}>
          Registrar conversão
        </Button>
      </div>
    </form>
  );
}
